import * as html from './html';
import { GetIconLibrary, IconMarkup } from './icons';

export function Issuer(cc: string): string {
    // the regular expressions check for possible matches as you type, hence the OR operators based on the number of chars
    // regexp string length {0} provided for soonest detection of beginning of the card numbers this way it could be used for BIN CODE detection also

    // JCB
    const jcb = new RegExp('^(?:2131|1800|35)[0-9]{0,}$'); // 2131, 1800, 35 (3528-3589)
    // American Express
    const amex = new RegExp('^3[47][0-9]{0,}$'); // 34, 37
    // Diners Club
    const diners = new RegExp('^3(?:0[0-59]{1}|[689])[0-9]{0,}$'); // 300-305, 309, 36, 38-39
    // Visa
    const visa = new RegExp('^4[0-9]{0,}$'); // 4
    // MasterCard
    const mastercard = new RegExp('^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)[0-9]{0,}$'); // 2221-2720, 51-55
    const maestro = new RegExp('^(5[06789]|6)[0-9]{0,}$'); // always growing in the range: 60-69, started with / not something else, but starting 5 must be encoded as mastercard anyway
    // Discover
    const discover = new RegExp('^(6011|65|64[4-9]|62212[6-9]|6221[3-9]|622[2-8]|6229[01]|62292[0-5])[0-9]{0,}$');
    //// 6011, 622126-622925, 644-649, 65

    // get rid of anything but numbers
    const curVal = cc.replace(/\D/g, '');

    // checks per each, as their could be multiple hits
    // fix: ordering matter in detection, otherwise can give false results in rare cases
    let selBrand: string = '';
    if (jcb.exec(curVal)) {
        selBrand = 'JCB';
    } else if (amex.exec(curVal)) {
        selBrand = 'Amex';
    } else if (diners.exec(curVal)) {
        selBrand = 'Diners Club';
    } else if (visa.exec(curVal)) {
        selBrand = 'Visa';
    } else if (mastercard.exec(curVal)) {
        selBrand = 'Mastercard';
    } else if (discover.exec(curVal)) {
        selBrand = 'Discover';
    } else if (maestro.exec(curVal)) {
        if (curVal.startsWith('5')) { // started 5 must be mastercard
            selBrand = 'Mastercard';
        } else {
            selBrand = 'Maestro'; // maestro is all 60-69 which is not something else, thats why this condition in the end
        }
    }

    return selBrand;
}

// GSA SmartPay charge-card prefixes, per https://smartpay.gsa.gov/smarttax/recognizing-your-account/
// Purchase and fleet accounts are centrally billed (the federal government pays the bank
// directly), so they should be sales-tax exempt in most states. Travel accounts share most of
// these prefixes; for the travel-only prefixes the 6th digit tells centrally billed (0, 5-9,
// exempt) from individually billed (1-4, the cardholder pays and is reimbursed, so state sales
// tax usually applies). WEX/Voyager fleet prefixes are omitted — our checkouts can't tokenize them.
const gsaSmartPayPrefixes = new Set(['4486', '4614', '4615', '4715', '4716', '5563', '5565', '5568']);
const gsaTravelOnlyPrefixes = new Set(['4486', '4615']);
const gsaIndividuallyBilledDigits = new Set(['1', '2', '3', '4']);

// IsGSASmartPay reports whether a card's first 6 digits (its BIN) look like a GSA SmartPay
// government charge card whose purchases are plausibly sales-tax exempt. The BIN is a strong
// hint, not proof — callers should warn, never auto-exempt or block.
export function IsGSASmartPay(first6: string): boolean {
    const digits = first6.replace(/\D/g, '');
    if (digits.length < 6) {
        return false;
    }

    const prefix = digits.slice(0, 4);
    if (!gsaSmartPayPrefixes.has(prefix)) {
        return false;
    }
    if (gsaTravelOnlyPrefixes.has(prefix) && gsaIndividuallyBilledDigits.has(digits[5])) {
        return false;
    }

    return true;
}

const faIssuerIcons: Record<string, string> = {
    'JCB': 'fa-cc-jcb',
    'Amex': 'fa-cc-amex',
    'Diners Club': 'fa-cc-diners-club',
    'Visa': 'fa-cc-visa',
    'Mastercard': 'fa-cc-mastercard',
    'Discover': 'fa-cc-discover',
};

export function IssuerIcon(cc: string): HTMLElement {
    const faIcon = faIssuerIcons[Issuer(cc)];
    if (!faIcon) {
        throw new Error('unknown credit card issuer');
    }
    switch (GetIconLibrary()) {
    case 'fontAwesome4':
        return html.FromString(`<i class="fa ${faIcon}"></i>`).firstElementChild as HTMLElement;
    case 'fontAwesome5':
        return html.FromString(`<i class="fab ${faIcon}"></i>`).firstElementChild as HTMLElement;
    case 'materialSymbolsRounded':
    case 'materialSymbolsSharp':
    case 'bootstrapIcons':
        // neither Material Symbols nor Bootstrap Icons draws card brands
        return html.FromString(IconMarkup({ fa4: '', fa5: '', ms: 'credit_card', bi: 'bi-credit-card' })).firstElementChild as HTMLElement;
    }
}
